import { i18n } from "@lingui/core"

export interface Option {
  label: string
  value: string
}

export function getExpenseEstimateOptions() {
  return [
    {
      label: i18n._("Less than $10,000"),
      value: "< $10K",
    },
    {
      label: i18n._("$10,000 - $100,000"),
      value: "$10k - $100k",
    },
    {
      label: i18n._("$100,000 - $500,000"),
      value: "$100k - $500k",
    },
    {
      label: i18n._("$500,000+"),
      value: "> $500k",
    },
  ] as const
}

export function getExpenseEstimateOptionsWithUnsure() {
  return [
    ...getExpenseEstimateOptions(),
    {
      label: i18n._("Unsure / Unaware"),
      value: "Unsure or Unaware",
    },
  ] as const
}

export type ExpenseEstimateWithUnsureOptions = ReturnType<
  typeof getExpenseEstimateOptionsWithUnsure
>[number]["value"]

export function getPayoutRecipientsOptions() {
  return [
    {
      label: i18n._("Research Participants"),
      value: "Research Compensation",
    },
    {
      label: i18n._("Employees"),
      value: "Employee Recognition",
    },
    {
      label: i18n._("Customers/Prospects"),
      value: "Marketing Incentives",
    },
    {
      label: i18n._("Other"),
      value: "Other",
    },
  ] as const
}

// DEMO FORM options
export const OTHER_VALUE = "Other"

export function getLeadOptions() {
  return [
    {
      label: i18n._("Search engine (Google, Bing)"),
      value: "Search engine",
    },
    {
      label: i18n._("Word of mouth (colleague, friend, family)"),
      value: "Word of mouth",
    },
    {
      label: i18n._("Conference or event"),
      value: "Conference or event",
    },
    {
      label: i18n._("YouTube or online video"),
      value: "YouTube or online video",
    },
    {
      label: i18n._("Social media (LinkedIn, Facebook)"),
      value: "Social media",
    },
    {
      label: i18n._("Review website (G2, Capterra, SourceForge)"),
      value: "Review website",
    },
    {
      label: i18n._("Platform integration or partner vendor"),
      value: "Platform integration or partner vendor",
    },
    {
      label: i18n._("Received a Tremendous reward"),
      value: "Received a Tremendous reward",
    },
    {
      label: i18n._("I'm a current or former customer"),
      value: "Current or former customer",
    },
    {
      label: i18n._("News article"),
      value: "News article",
    },
    {
      label: i18n._("AI chatbot"),
      value: "AI chatbot",
    },
    {
      label: i18n._("Other"),
      value: OTHER_VALUE,
    },
  ] as const
}

export type LeadOptionValues = ReturnType<
  typeof getLeadOptions
>[number]["value"]

// CALCULATOR options
export function getStudyTypeOptions() {
  return [
    {
      label: i18n._("Online interview"),
      value: "Interview",
    },
    {
      label: i18n._("Online survey"),
      value: "Survey",
    },
  ] as const
}

// Name OK because it characterizes enum options, not a model record
// eslint-disable-next-line no-restricted-syntax
export type StudyType = ReturnType<typeof getStudyTypeOptions>[number]["value"]

export function getIncentiveTypeOptions() {
  return [
    {
      label: i18n._("Monetary transfer"),
      value: "Monetary transfer",
    },
    {
      label: i18n._("Digital Amazon gift card"),
      value: "Digital Amazon gift card",
    },
    {
      label: i18n._("Visa prepaid card"),
      value: "Visa prepaid card",
    },
    {
      label: i18n._("Gift card - participant's choice"),
      value: "Gift card - participant's choice",
    },
    {
      label: i18n._("Mailed check"),
      value: "Mailed check",
    },
  ] as const
}

// Name OK because it characterizes enum options, not a model record
// eslint-disable-next-line no-restricted-syntax
export type IncentiveType = ReturnType<
  typeof getIncentiveTypeOptions
>[number]["value"]

export function getSensitivityLevelOptions() {
  return [
    {
      label: i18n._("Non-sensitive subject matter"),
      value: "Non-sensitive subject matter",
    },
    {
      label: i18n._("Sensitive subject matter"),
      value: "Sensitive subject matter",
    },
  ] as const
}

export type SensitivityLevel = ReturnType<
  typeof getSensitivityLevelOptions
>[number]["value"]

export function getAudienceOptions() {
  return [
    {
      label: i18n._("Student"),
      value: "Student",
    },
    {
      label: i18n._("Professional"),
      value: "Professional",
    },
    {
      label: i18n._("Consumer"),
      value: "Consumer",
    },
    {
      label: i18n._("Unemployed"),
      value: "Unemployed",
    },
  ] as const
}

export type Audience = ReturnType<typeof getAudienceOptions>[number]["value"]

export function getIncomeRangeOptions() {
  return [
    {
      label: i18n._("$0 - $49,999"),
      value: "$0 - $49,999",
    },
    {
      label: i18n._("$50,000 - $99,999"),
      value: "$50,000 - $99,999",
    },
    {
      label: i18n._("$100,000 - $149,999"),
      value: "$100,000 - $149,999",
    },
    {
      label: i18n._("$150,000 - $199,999"),
      value: "$150,000 - $199,999",
    },
    {
      label: i18n._("$200,000+"),
      value: "$200,000+",
    },
  ] as const
}

export type IncomeRange = ReturnType<
  typeof getIncomeRangeOptions
>[number]["value"]

export function getResearchTypeOptions() {
  return [
    {
      label: i18n._("Market research"),
      value: "Consumer or market research",
    },
    {
      label: i18n._("UX research"),
      value: "UX/UI research",
    },
    {
      label: i18n._("Academic research"),
      value: "Academic research",
    },
    {
      label: i18n._("Clinical research"),
      value: "Medical research",
    },
    {
      label: i18n._("Other"),
      value: "Other research program",
    },
  ] as const
}

// Name OK because it characterizes enum options, not a model record
// eslint-disable-next-line no-restricted-syntax
export type ResearchType = ReturnType<
  typeof getResearchTypeOptions
>[number]["value"]

export function getDeliveryMethodOptions() {
  return [
    { label: i18n._("Email"), value: "email" },
    { label: i18n._("SMS (text message)"), value: "phone" },
  ] as const
}

export function getLabelByValue(value: string, options: readonly Option[]) {
  return options.find(item => item.value === value)?.label || ""
}
